import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";

type ConversionSetting = InterludeTypes["ConversionSettingResponse"];

interface EditConversionSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accountId: number;
  conversionSettings: Array<ConversionSetting> | null;
}

const cssModalContent = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const cssDescription = css`
  margin-bottom: 8px;
`;

const cssCategoryRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
`;

const cssActionRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  padding-left: 36px;
  border-radius: 4px;
`;

const cssCheckbox = css`
  padding: 0;
  width: 16px;
  height: 16px;
  & .MuiSvgIcon-root {
    font-size: 16px;
  }
`;

export const EditConversionSettingsModal: React.FC<EditConversionSettingsModalProps> =
  function EditConversionSettingsModal({
    open,
    onClose,
    onSuccess,
    accountId,
    conversionSettings,
  }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [checkedSettings, setCheckedSettings] = useState<Set<string>>(() => {
      const initialChecked = new Set<string>();
      if (conversionSettings) {
        conversionSettings.forEach((setting) => {
          if (setting.createdAt !== null) {
            initialChecked.add(setting.conversionActionId);
          }
        });
      }
      return initialChecked;
    });

    const updateMutation = interlude.googleAds.useUpdateConversionSettings(
      {
        params: {
          googleAdsAccountId: accountId,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(
            t("applications.googleAds.conversionSettings.updateSuccess"),
            {
              variant: "success",
            },
          );
          onSuccess();
        },
        onError: () => {
          enqueueSnackbar(
            t("applications.googleAds.conversionSettings.updateError"),
            {
              variant: "error",
            },
          );
        },
      },
    );

    const handleToggle = useHandler(
      (setting: ConversionSetting, checked: boolean) => {
        if (checked) {
          setCheckedSettings((prev) => {
            const next = new Set(prev);
            next.add(setting.conversionActionId);
            return next;
          });
        } else {
          setCheckedSettings((prev) => {
            const next = new Set(prev);
            next.delete(setting.conversionActionId);
            return next;
          });
        }
      },
    );

    const handleCommerceToggle = useHandler((checked: boolean) => {
      if (!conversionSettings) return;
      conversionSettings.forEach((setting) => {
        const isCurrentlyChecked = checkedSettings.has(
          setting.conversionActionId,
        );
        if (checked && !isCurrentlyChecked) {
          handleToggle(setting, true);
        } else if (!checked && isCurrentlyChecked) {
          handleToggle(setting, false);
        }
      });
    });

    const handleDone = useHandler(() => {
      const conversionActionIds = Array.from(checkedSettings);
      updateMutation.mutate({
        conversionActionIds,
      });
      onClose();
    });

    const settingsList = useMemo(
      () => conversionSettings ?? [],
      [conversionSettings],
    );

    const isCommerceChecked = useMemo(() => {
      if (settingsList.length === 0) return false;
      return settingsList.every((setting) =>
        checkedSettings.has(setting.conversionActionId),
      );
    }, [settingsList, checkedSettings]);

    const isCommerceIndeterminate = useMemo(() => {
      if (settingsList.length === 0) return false;
      const checkedCount = settingsList.filter((setting) =>
        checkedSettings.has(setting.conversionActionId),
      ).length;
      return checkedCount > 0 && checkedCount < settingsList.length;
    }, [settingsList, checkedSettings]);

    const formatSettingLabel = (setting: ConversionSetting): string => {
      return t("applications.googleAds.conversionSettings.modal.purchase", {
        customizedName: setting.conversionActionName,
      });
    };

    return (
      <Modal
        open={open}
        handleClose={onClose}
        title={t("applications.googleAds.conversionSettings.modal.title")}
        width={520}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={handleDone}>
              {t("common.done")}
            </Button>
          </Stack>
        }
      >
        <div css={cssModalContent}>
          <Typography
            variant="body"
            color={theme.colors.staticFgBody}
            css={cssDescription}
          >
            {t("applications.googleAds.conversionSettings.modal.description")}
          </Typography>

          {settingsList.length === 0 ? (
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t("applications.googleAds.conversionSettings.modal.empty")}
            </Typography>
          ) : (
            <div>
              <div css={cssCategoryRow}>
                <Checkbox
                  css={cssCheckbox}
                  checked={isCommerceChecked}
                  indeterminate={isCommerceIndeterminate}
                  onChange={(e) => {
                    handleCommerceToggle(e.target.checked);
                  }}
                />
                <Typography
                  variant="body"
                  fontWeight={500}
                  color={theme.colors.staticFgBody}
                >
                  {t(
                    "applications.googleAds.conversionSettings.modal.commerce",
                  )}
                </Typography>
              </div>

              {settingsList.map((setting) => {
                const isChecked = checkedSettings.has(
                  setting.conversionActionId,
                );

                return (
                  <div key={setting.conversionActionId} css={cssActionRow}>
                    <Checkbox
                      css={cssCheckbox}
                      checked={isChecked}
                      onChange={(e) => {
                        handleToggle(setting, e.target.checked);
                      }}
                    />
                    <Typography
                      variant="body"
                      color={theme.colors.staticFgBody}
                    >
                      {formatSettingLabel(setting)}
                    </Typography>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    );
  };
